import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ButtonDirective} from "primeng/button";
import {TooltipModule} from "primeng/tooltip";
import {Dialog} from 'primeng/dialog';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {IconPathPipe} from "@app/utils/pipes/icon-path-pipe";
import {environment} from '@root/src/environments/environment';
import {IPolygonColor} from '@components/request-map/request-map.component2';
import {MsfSelect} from '@components/select/select';
import {DatePickerComponent} from '@components/date-picker/date-picker.component';
import {SatelliteStyleSelectorComponent} from '@components/satellite-style-selector/satellite-style-selector.component';
import {CheckboxComponent} from '@components/checkbox';
import {IndexManager} from '@components/openstreetmap/managers/index.manager';
import {INDEX_LAYER_OPTIONS, IndexLayerOption} from '@shared/data/index-layer-options-data';
import {OSM_SATELLITE_MENU_ITEMS} from '@components/openstreetmap/constants/osm-map-baselayer.util';
import {IndexType} from '@services/satellite-indices.service';
import type {OldMapBaseLayer} from './managers/map.manager';
import {
  MapManager,
  PolygonManager,
  ParentPolygonManager,
  SplitManager,
  PolygonCallbacks,
  HistoryManager,
  SiblingPolygonManager,
  SiblingPolygon,
  SplitPolygonItem
} from './managers';
import {
  extractCoordinates,
  olCalculateAreaFromPolygonInHectares,
  olParsePolygonJson
} from '@app/utils/polygons.utils';
import polygonClipping from 'polygon-clipping';
import {MessageService} from 'primeng/api';
import {Toast} from 'primeng/toast';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';

declare var ol: any;


@Component({
  selector: 'mfs-old-map',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonDirective,
    TooltipModule,
    TranslateModule,
    IconPathPipe,
    Toast,
    SecondaryButton,
    Dialog,
    MsfSelect,
    DatePickerComponent,
    SatelliteStyleSelectorComponent,
    CheckboxComponent,
  ],
  templateUrl: './old-map.component.html',
  styleUrls: ['./old-map.component.scss'],
  providers: [MessageService]
})
export class OldMapComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('mapContainer', {static: true}) mapContainer!: ElementRef;

  private readonly indexManager = inject(IndexManager);
  private readonly destroy$ = new Subject<void>();

  // Managers
  public mapManager!: MapManager;
  private polygonManager!: PolygonManager;
  private parentPolygonManager!: ParentPolygonManager;
  private splitManager!: SplitManager;
  private historyManager!: HistoryManager;
  private siblingPolygonManager!: SiblingPolygonManager;

  private isDrawing = false;
  private ndiLayer: any;
  public mapLoaded = false;
  public canUndo = false;

  private _polygonJson: string | undefined = undefined;
  private _multiPolygonJson: string[] = [];
  private _parentPolygonJson: string | undefined = undefined;
  private _siblingPolygons: SiblingPolygon[] = [];

  @Input() height = '100%';
  @Input() center: [any?, any?] = [environment.map.lng, environment.map.lat];
  @Input() zoom = 13;
  @Input() drawingMode = false;
  @Input() satellite: boolean = false;
  @Input() zoomControl: boolean = true;
  @Input() isCenterMarker: boolean = true;
  @Input() isCenterControl: boolean = true;
  @Input() isSplitControl: boolean = true;
  @Input() polygonArea: number | null | undefined= 300;
  @Input() isLoading = false;
  @Input() fitAllSibling = true;
  // TODO:@francis  remove this input if not used
  @Input() allowRightClickExit = false;
  @Input() allowDrawUnderSibling = false;
  @Input() allowSelfIntersection = false;

  @Input() polygonColor = "rgba(39, 237, 0, 0.44)"
  /** When set, draws diagonal hatch in this colour on top of `polygonColor` (OpenLayers fill pattern). */
  @Input() polygonStripeColor: string | null = null;
  @Input() parentPolygonColor = "rgba(100, 100, 100, 0.2)"
  /** Quand true : remplissage parent transparent (seul le contour reste), ex. image WMS sous-jacente. */
  @Input() indexRasterOverlayActive = false;

  /** XYZ tile URL template (with {x}/{y}/{z}) for the vegetation-index raster overlay. Null hides it. */
  @Input() set indexTileUrl(url: string | null) {
    this._indexTileUrl = url || null;
    if (this.mapLoaded) {
      this.updateIndexTileLayer();
    }
  }
  get indexTileUrl(): string | null { return this._indexTileUrl; }
  private _indexTileUrl: string | null = null;

  /** Opacity (0–1) of the index raster overlay. */
  @Input() set indexTileOpacity(value: number) {
    this._indexTileOpacity = value;
    this.ndiLayer?.setOpacity?.(value);
  }
  get indexTileOpacity(): number { return this._indexTileOpacity; }
  private _indexTileOpacity = 0.75;

  /** Show the built-in index button (modal + opacity). Hidden in field-details and passport-general-map. */
  @Input() showIndexControl = true;

  /** Show the polygon fill visibility toggle (eye menu, polygon only). */
  @Input() showPolygonFillToggle = true;

  // --- Polygon fill visibility (outline only) ---
  polygonFillMenuOpen = false;
  polygonFillVisible = true;

  // --- Base layer (satellite) selector state ---
  readonly satelliteMenuItems = [...OSM_SATELLITE_MENU_ITEMS];
  satelliteSelectorVisible = false;
  currentBaseLayer: OldMapBaseLayer = 'google';
  currentSatelliteLabel = 'Satellite';

  // --- Index overlay control state ---
  readonly indexLayerOptions: IndexLayerOption[] = INDEX_LAYER_OPTIONS;
  showIndexModal = false;
  selectedIndexLayer: string | null = null;
  selectedIndexDate: Date | null = null;
  indexAvailableDates: string[] | null = [];
  currentYear = new Date().getFullYear();
  /** Index overlay opacity as a percentage (0–100) for the slider. */
  indexLayerOpacityPercent = 75;
  @Input() siblingPolygonColor = "rgba(66, 135, 245, 1)";
  @Input() canSave = true;
  @Input() geometry!: string;
  @Input() showControls = true;
  @Input() showCopyParentBtn = false;
  @Input() showCopyParentContourLabel = 'Copy';

  /** Optional name/label rendered in the centre of the main polygon */
  @Input() set polygonName(name: string | null | undefined) {
    this._polygonName = name ?? null;
    if (this.polygonManager) {
      this.polygonManager.polygonName = this._polygonName;
      // Refresh the existing polygon style to show / hide the label
      this.polygonManager.updatePolygonColor(this.polygonColor);
    }
  }
  get polygonName(): string | null { return this._polygonName; }
  private _polygonName: string | null = null;
  @Output() polygonJsonChange = new EventEmitter<any>();
  @Output() polygonAreaChange = new EventEmitter<any>();
  @Output() canSaveChange = new EventEmitter<any>();
  @Output() validationError = new EventEmitter<any>();
  @Output() polygonSplit = new EventEmitter<string[]>();
  @Output() boundaryViolation = new EventEmitter<string>();

  /** Two-way binding: list of split polygon items (updated after every split / modification) */
  @Input() splitPolygons: SplitPolygonItem[] = [];
  @Output() splitPolygonsChange = new EventEmitter<SplitPolygonItem[]>();

  /** Labels to display on split polygons (index 0 → 'old', index 1 → 'new'). Null = no label. */
  @Input() set splitLabels(labels: (string | null)[] | null | undefined) {
    const resolved = labels ?? [null, null];
    if (this.splitManager) {
      this.splitManager.labels = resolved;
    }
    this._splitLabels = resolved;
  }
  get splitLabels(): (string | null)[] { return this._splitLabels; }
  private _splitLabels: (string | null)[] = [null, null];
  @Output() siblingOverlap = new EventEmitter<{ overlaps: boolean; siblingIds: (string | number)[] }>();


  @Output() coordinatesChange = new EventEmitter<{ lat: number; lng: number }[]>();
  @Output() markerClick = new EventEmitter<{ lat: number; lng: number }>();
  @Output() mapClick = new EventEmitter<{ lat: number; lng: number }>();
  @Output() mapMove = new EventEmitter<any>();
  @Output() geometryChange = new EventEmitter<any>();

  @Input() set parentPolygonJson(json: string | null | undefined) {
    this._parentPolygonJson = json as string;
    if (json && this.mapLoaded) {
      setTimeout(() => {
        this.parentPolygonManager.drawParentPolygon(json, j => olParsePolygonJson(j));
        this.applyIndexRasterOverlayParentStyle();
      }, 100);
    }
  }

  get parentPolygonJson(): string | undefined {
    return this._parentPolygonJson;
  }

  @Input() set polygonJson(json: string | undefined | null) {
    this._polygonJson = json as string;
    if (json && this.mapLoaded) {
      setTimeout(() => {
        this.polygonManager?.drawPolygonFromJson(json, this.polygonColor, this.polygonArea);
        // Sauvegarder l'état initial pour l'historique
        this.saveCurrentStateToHistory();
      }, 500);
    }
  }

  @Input() set multiPolygonJsonColors(jsons: IPolygonColor[] | undefined) {
    if (jsons) {
      setTimeout(() => {
        this._multiPolygonJson = [];
        this.polygonManager?.drawPolygonFromJsonAndFitView(jsons);
      }, 500);
    }
  }

  /**
   * Setter pour les polygones voisins (autres plantations sur le champ)
   */
  @Input() set siblingPolygons(siblings: SiblingPolygon[] | undefined) {
    this._siblingPolygons = siblings || [];
    if (this.mapLoaded && this.siblingPolygonManager) {
      this.siblingPolygonManager.clearAllSiblings();
      if (siblings && siblings.length > 0) {
        this.siblingPolygonManager.addSiblingPolygons(siblings, this.fitAllSibling);
      }
    }
  }

  get siblingPolygons(): SiblingPolygon[] {
    return this._siblingPolygons;
  }


  // Public accessors for template
  get splitMode(): boolean {
    return this.splitManager?.isSplitMode ?? false;
  }

  /** Whether the map currently has active split polygons */
  get hasSplit(): boolean {
    return this.splitManager?.hasSplit ?? false;
  }


  /**
   * Écoute la combinaison Ctrl+Z pour annuler la dernière modification
   */
  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.polygonFillMenuOpen) {
      return;
    }
    const target = event.target as Element | null;
    if (target && typeof target.closest === 'function' && target.closest('.polygon-fill-tool-wrapper')) {
      return;
    }
    this.polygonFillMenuOpen = false;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Ctrl+Z (Windows/Linux) ou Cmd+Z (Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
      if (this.canUndo || this.hasSplit) {
        event.preventDefault();
        this.undo();
      }
    }
  }

  constructor(
    private _translate: TranslateService,
    private _messageService: MessageService,
  ) {
  }

  ngOnInit() {
    if (!document.querySelector('link[href*="ol.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/gh/openlayers/openlayers.github.io@master/en/v6.15.1/css/ol.css';
      document.head.appendChild(link);
    }

    if (typeof ol === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/openlayers/openlayers.github.io@master/en/v6.15.1/build/ol.js';
      script.onload = () => {
        console.log('OpenLayers loaded successfully');
        this.initializeMap();
      };
      script.onerror = () => {
        console.error('Failed to load OpenLayers');
      };
      document.body.appendChild(script);
    }
  }

  ngAfterViewInit() {
    if (typeof ol !== 'undefined') {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['center'] && this.mapManager && !changes['center'].firstChange) {
      const coords = changes['center'].currentValue as [number, number];
      this.mapManager.updateCenterMarker(coords[0], coords[1], this.isCenterMarker);
    }

    if (this.polygonManager && (changes['polygonColor'] || changes['polygonStripeColor'])) {
      if (changes['polygonStripeColor']) {
        this.polygonManager.setStripeOverlayColor(this.polygonStripeColor ?? null);
      }
      if (changes['polygonColor'] || changes['polygonStripeColor']) {
        this.polygonManager.updatePolygonColor(this.polygonColor);
      }
    }

    if (changes['drawingMode'] && !changes['drawingMode'].firstChange) {
      if (changes['drawingMode'].currentValue === true) {
        this.polygonManager.enableEditPolygon(true, this.parentPolygonManager.hasParent);
      } else {
        this.polygonManager.disableEditPolygon();
      }
    }

    if (changes['siblingPolygons'] && this.siblingPolygonManager && !changes['siblingPolygons'].firstChange) {
      this.siblingPolygonManager.clearAllSiblings();
      const siblings = changes['siblingPolygons'].currentValue as SiblingPolygon[];
      if (siblings && siblings.length > 0) {
        this.siblingPolygonManager.addSiblingPolygons(siblings);
      }
    }

    if (changes['allowDrawUnderSibling'] && this.polygonManager && !changes['allowDrawUnderSibling'].firstChange) {
      this.polygonManager.setAllowDrawUnderSibling(changes['allowDrawUnderSibling'].currentValue);
    }

    if (changes['allowSelfIntersection'] && this.polygonManager && !changes['allowSelfIntersection'].firstChange) {
      this.polygonManager.setAllowSelfIntersection(changes['allowSelfIntersection'].currentValue);
    }

    if (changes['indexRasterOverlayActive'] && this.parentPolygonManager) {
      this.applyIndexRasterOverlayParentStyle();
    }
  }

  private applyIndexRasterOverlayParentStyle(): void {
    if (!this.parentPolygonManager?.hasParent) {
      return;
    }
    this.parentPolygonManager.setParentFillHidden(this.indexRasterOverlayActive);
  }

  /** (Re)build the index XYZ tile layer from {@link indexTileUrl}, kept below the vector polygons. */
  private updateIndexTileLayer(): void {
    if (!this.mapManager || typeof ol === 'undefined') {
      return;
    }
    if (this.ndiLayer) {
      this.mapManager.removeLayer(this.ndiLayer);
      this.ndiLayer = null;
    }
    if (!this._indexTileUrl) {
      return;
    }
    this.ndiLayer = new ol.layer.Tile({
      source: new ol.source.XYZ({
        url: this._indexTileUrl,
        crossOrigin: 'anonymous',
      }),
      opacity: this._indexTileOpacity,
    });
    this.mapManager.addIndexTileLayer(this.ndiLayer);
  }

  private initializeManagers(): void {
    // Initialize Parent Polygon Manager
    this.parentPolygonManager = new ParentPolygonManager(
      () => this.mapManager.mapInstance,
      this.parentPolygonColor
    );

    // Initialize History Manager
    this.historyManager = new HistoryManager(
      () => this.mapManager.vectorSourceInstance,
      (canUndo: any) => {
        this.canUndo = canUndo;
      }
    );

    // Initialize Polygon Manager with callbacks
    const callbacks: PolygonCallbacks = {
      coordinatesChange: this.coordinatesChange,
      polygonAreaChange: this.polygonAreaChange,
      geometryChange: this.geometryChange,
      validationError: this.validationError,
      boundaryViolation: this.boundaryViolation,
      canSave: this.canSaveChange,
    };

    // Initialize Sibling Polygon Manager BEFORE PolygonManager (needed for isInsideSibling callback)
    this.siblingPolygonManager = new SiblingPolygonManager(
      () => this.mapManager.mapInstance,
      (json) => olParsePolygonJson(json)
    );

    this.polygonManager = new PolygonManager(
      () => this.mapManager.mapInstance,
      () => this.mapManager.vectorSourceInstance,
      this.polygonColor,
      callbacks,
      (coord) => this.parentPolygonManager.isPointInsideParentPolygon(coord),
      (geom) => this.parentPolygonManager.isPolygonInsideParentPolygon(geom),
      (coord) => this.parentPolygonManager.clipCoordinateToParent(coord),
      this._translate,
      (coord) => this.siblingPolygonManager.isPointInsideSibling(coord),
      this._messageService,
      (geom) => this.siblingPolygonManager.doesPolygonOverlapSibling(geom)
    );

    this.polygonManager.setStripeOverlayColor(this.polygonStripeColor ?? null);

    // Configurer allowDrawUnderSibling
    this.polygonManager.setAllowDrawUnderSibling(this.allowDrawUnderSibling);

    // Configurer allowSelfIntersection
    this.polygonManager.setAllowSelfIntersection(this.allowSelfIntersection);

    // Apply polygonName label if already set via input
    this.polygonManager.polygonName = this._polygonName;

    // Configurer le callback pour sauvegarder l'état dans l'historique après chaque modification
    this.polygonManager.setOnModifyEndCallback((feature: any) => {
      this.historyManager.saveState(feature);
    });


    // Initialize Split Manager
    this.splitManager = new SplitManager(
      () => this.mapManager.mapInstance,
      () => this.mapManager.vectorSourceInstance,
      this.polygonSplit,
      () => this.polygonManager.enableEditPolygon(this.drawingMode, this.parentPolygonManager.hasParent),
      (items: SplitPolygonItem[]) => {
        this.splitPolygons = items;
        this.splitPolygonsChange.emit(items);
      }
    );
    // Apply labels if already set via input
    this.splitManager.labels = this._splitLabels;
  }

  private initializeMap() {
    if (!this.mapContainer || this.mapManager?.mapInstance) return;

    try {
      if (typeof ol === 'undefined') return;

      this.mapManager = new MapManager(
        this.mapContainer.nativeElement,
        this.center as [number, number],
        this.zoom
      );

      if (!this.mapManager.initializeMap()) return;

      this.initializeManagers();
      this.initMapOnclick();

      if (this.center && this.center[0] !== undefined && this.center[1] !== undefined) {
        this.mapManager.updateCenterMarker(this.center[0], this.center[1], this.isCenterMarker);
      }


      // this.mapManager.on('moveend', () => {});


      const emitMapMove = () => {
        const view = this.mapManager.getView();
        const zoom = view.getZoom();
        const extent = view.calculateExtent(this.mapManager.getSize());
        const bottomLeft = ol.proj.toLonLat([extent[0], extent[1]]);
        const topRight = ol.proj.toLonLat([extent[2], extent[3]]);

        this.mapMove.emit({
          zoom: zoom,
          bounds: {
            south: bottomLeft[1],
            north: topRight[1],
            west: bottomLeft[0],
            east: topRight[0]
          }
        });
      };

      // Listen to map move/pan events
      this.mapManager.on('moveend', emitMapMove);
      this.mapLoaded = true;
      this.drawPolygonAfterMapLoad();
      this.updateIndexTileLayer();

      if (this.satellite) {
        this.mapManager.toggleSatelliteLayer('satellite');
      }

      // Charger les polygones voisins si définis
      if (this._siblingPolygons.length > 0) {
        this.siblingPolygonManager.addSiblingPolygons(this._siblingPolygons);
      }
    } catch (error) {
      console.error('Error initializing OpenLayers map:', error);
    }
  }

  private initMapOnclick() {
    this.mapManager.on('click', (evt: any) => {
      const coordinate = evt.coordinate;
      const lonLat = ol.proj.toLonLat(coordinate);

      if (this.drawingMode) {
        if (!this.isDrawing) {
          this.isDrawing = true;
        }
      } else {
        this.markerClick.emit({lat: lonLat[1], lng: lonLat[0]});
      }
    });
  }

  private drawPolygonAfterMapLoad() {
    if (this._parentPolygonJson) {
      this.parentPolygonManager.drawParentPolygon(this._parentPolygonJson, j => olParsePolygonJson(j));
      this.applyIndexRasterOverlayParentStyle();
    }

    if (this._polygonJson) {
      this.polygonManager.drawPolygonFromJson(this._polygonJson, undefined, this.polygonArea);
      // Sauvegarder l'état initial
      setTimeout(() => this.saveCurrentStateToHistory(), 100);
    }

    if (this._multiPolygonJson.length > 0) {
      this._multiPolygonJson.forEach(j => this.polygonManager.drawPolygonFromJson(j));
    }

    this.polygonManager.enableEditPolygon(this.drawingMode, this.parentPolygonManager.hasParent);
  }

  /**
   * Sauvegarde l'état actuel du polygone dans l'historique
   */
  private saveCurrentStateToHistory(): void {
    const vectorSource = this.mapManager?.vectorSourceInstance;
    if (!vectorSource) return;

    const polygonFeatures =
      vectorSource.getFeatures()
        .filter((f: any) => f.getGeometry()?.getType() === 'Polygon' &&
          f.get('type') !== 'parentPolygon' &&
          f.get('type') !== 'siblingPolygon');

    if (polygonFeatures.length > 0) {
      this.historyManager.saveState(polygonFeatures[0]);
    }
  }

  /**
   * Annule la dernière modification (Ctrl+Z)
   * If a split has been performed, undo restores the original polygon first.
   */
  public undo(): void {
    // If a split is active, restore original polygon instead of normal undo
    if (this.splitManager?.hasSplit) {
      if (this.splitManager.restoreOriginal()) {
        // Re-enable edit mode on the restored polygon
        this.polygonManager.enableEditPolygon(this.drawingMode, this.parentPolygonManager.hasParent);
        // Save restored polygon geometry
        const vectorSource = this.mapManager.vectorSourceInstance;
        const polygonFeatures = vectorSource.getFeatures()
          .filter((f: any) => f.getGeometry()?.getType() === 'Polygon' &&
            f.get('type') !== 'parentPolygon' &&
            f.get('type') !== 'siblingPolygon');
        if (polygonFeatures.length > 0) {
          this.polygonManager.savePolygon(polygonFeatures[0], this.parentPolygonManager.hasParent);
        }
      }
      return;
    }

    if (this.historyManager.undo()) {
      // Après l'undo, sauvegarder le nouveau polygone
      const vectorSource = this.mapManager.vectorSourceInstance;
      const polygonFeatures = vectorSource.getFeatures()
        .filter((f: any) => f.getGeometry()?.getType() === 'Polygon' &&
          f.get('type') !== 'parentPolygon' &&
          f.get('type') !== 'siblingPolygon');

      if (polygonFeatures.length > 0) {
        this.polygonManager.savePolygon(polygonFeatures[0], this.parentPolygonManager.hasParent);
      }
    }
  }

  copyParentPolygon() {
    if (!this._parentPolygonJson) return;

    // If siblings exist, compute the free space: parent - union(siblings)
    if (this.siblingPolygonManager?.hasSiblings) {
      const freeSpaceWkt = this.computeFreeSpaceInParent();
      if (freeSpaceWkt) {
        this.polygonJson = freeSpaceWkt;
        this.geometryChange.emit(freeSpaceWkt);
        const area = olCalculateAreaFromPolygonInHectares(extractCoordinates(freeSpaceWkt), ol);
        this.polygonAreaChange.emit(area?.toFixed(2));
        return;
      }
      // Fallback: if difference computation failed, copy parent as-is
    }

    this.polygonJson = this._parentPolygonJson;
    this.geometryChange.emit(this._parentPolygonJson);

    const area = olCalculateAreaFromPolygonInHectares(extractCoordinates(this._parentPolygonJson), ol);
    this.polygonAreaChange.emit(area?.toFixed(2));
  }

  /**
   * Computes the free area inside the parent polygon that is not occupied by any sibling.
   * Returns WKT string of the resulting polygon, or null if computation fails.
   */
  private computeFreeSpaceInParent(): string | null {
    try {
      const parentCoords = olParsePolygonJson(this._parentPolygonJson!);
      if (!parentCoords || parentCoords.length < 3) return null;

      // polygon-clipping expects [lon, lat] pairs — olParsePolygonJson already returns [lon, lat]
      const parentRing: [number, number][] = parentCoords.map(c => [c[0], c[1]]);
      // Ensure the ring is closed
      if (parentRing[0][0] !== parentRing[parentRing.length - 1][0] ||
          parentRing[0][1] !== parentRing[parentRing.length - 1][1]) {
        parentRing.push([...parentRing[0]] as [number, number]);
      }

      const siblingRings = this.siblingPolygonManager.getAllSiblingCoordinatesLonLat();
      if (!siblingRings || siblingRings.length === 0) return null;

      // Build the subject polygon (parent)
      let result: number[][][][] = [[parentRing]];

      // Subtract each sibling polygon from the result
      for (const sibRing of siblingRings) {
        const closedSibRing: [number, number][] = [...sibRing];
        if (closedSibRing[0][0] !== closedSibRing[closedSibRing.length - 1][0] ||
            closedSibRing[0][1] !== closedSibRing[closedSibRing.length - 1][1]) {
          closedSibRing.push([...closedSibRing[0]] as [number, number]);
        }

        result = polygonClipping.difference(
          result as any,
          [[closedSibRing]] as any
        );
        if (!result || result.length === 0) return null;
      }

      // Take the first (largest) polygon from the result
      // result is MultiPolygon: Polygon[] where Polygon = Ring[] and Ring = [number, number][]
      const firstPolygon = result[0];
      if (!firstPolygon || firstPolygon.length === 0) return null;

      const outerRing = firstPolygon[0];
      const wktCoords = outerRing
        .map((c: number[]) => `${c[0]} ${c[1]}`)
        .join(', ');

      return `POLYGON ((${wktCoords}))`;
    } catch (error) {
      console.error('Error computing free space in parent:', error);
      return null;
    }
  }

  // Public API methods
  public startPolygonDrawing() {
    this.drawingMode = true;
    this.historyManager.clear();
    this.polygonManager.startPolygonDrawing(this.parentPolygonManager.hasParent);
  }




  public toggleSatelliteLayer(tile: 'plan' | 'satellite') {
    this.mapManager.toggleSatelliteLayer(tile);
  }

  // =========================================================================
  // BASE LAYER (SATELLITE) SELECTOR
  // =========================================================================

  selectPlanLayer(): void {
    this.currentBaseLayer = 'plan';
    this.satelliteSelectorVisible = false;
    this.mapManager?.setBaseLayer('plan');
  }

  selectSatelliteLayer(type: OldMapBaseLayer): void {
    if (type === 'plan') {
      this.selectPlanLayer();
      return;
    }
    this.currentBaseLayer = type;
    this.currentSatelliteLabel =
      type === 'google' ? 'Google' : type === 'sentinel' ? 'Sentinel' : 'Landsat';
    this.satelliteSelectorVisible = false;
    this.mapManager?.setBaseLayer(type);
  }

  get isPlanLayerActive(): boolean {
    return this.currentBaseLayer === 'plan';
  }

  // =========================================================================
  // INDEX OVERLAY CONTROL (modal + opacity)
  // =========================================================================

  openIndexModal(): void {
    this.showIndexModal = true;
  }

  closeIndexModal(): void {
    this.showIndexModal = false;
  }

  onIndexLayerChange(layer: string | null): void {
    this.selectedIndexLayer = layer;
    this.selectedIndexDate = null;
    this.indexAvailableDates = [];
    this.getAvailableIndexDates();
  }

  onIndexDateChange(date: Date | null): void {
    this.selectedIndexDate = date;
  }

  getAvailableIndexDates(year?: number): void {
    if (year) {
      this.currentYear = year;
    }
    if (!this.selectedIndexLayer) {
      return;
    }
    this.indexManager
      .GetIndexDatesFromTileLayers(this.selectedIndexLayer as IndexType, this.currentYear)
      .pipe(takeUntil(this.destroy$))
      .subscribe((dates: string[]) => {
        this.indexAvailableDates = dates;
      });
  }

  /** Resolve the GeoServer layer for the chosen index/date and show it as an XYZ overlay. */
  applyIndexSelection(): void {
    const idx = this.selectedIndexLayer;
    const date = this.selectedIndexDate;
    if (!idx || !date) {
      return;
    }
    this.isLoading = true;
    this.indexManager
      .loadAvailableIndices(date, idx)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (layers: any[]) => {
          this.isLoading = false;
          const geo = layers?.[0]?.geoServerName;
          const layerBase = geo ? String(geo).trim().replace(/^indexint8:/i, '').trim() : '';
          if (!layerBase) {
            this.indexTileUrl = null;
            return;
          }
          const style = `${idx.toUpperCase()}_int8`;
          this.indexTileUrl =
            `${environment.api}/tiles/Map/GetTileByLayer?x={x}&y={y}&z={z}` +
            `&layer=${encodeURIComponent(`indexint8:${layerBase}`)}` +
            `&style=${encodeURIComponent(style)}&coordinateSystem=0`;
          this.indexTileOpacity = this.indexLayerOpacityPercent / 100;
        },
        error: () => {
          this.isLoading = false;
          this.indexTileUrl = null;
        },
      });
    this.showIndexModal = false;
  }

  clearIndexSelection(): void {
    this.selectedIndexLayer = null;
    this.selectedIndexDate = null;
    this.indexTileUrl = null;
  }

  onIndexOpacityChange(percent: number): void {
    const pct = Math.max(0, Math.min(100, Number(percent) || 0));
    this.indexLayerOpacityPercent = pct;
    this.indexTileOpacity = pct / 100;
  }

  get hasActiveIndexLayer(): boolean {
    return !!this.indexTileUrl;
  }

  // =========================================================================
  // POLYGON FILL VISIBILITY (outline only)
  // =========================================================================

  togglePolygonFillMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.polygonFillMenuOpen = !this.polygonFillMenuOpen;
  }

  setPolygonFillVisible(visible: boolean): void {
    this.polygonFillVisible = visible;
    this.polygonManager?.setPolygonFillVisible(visible);
  }

  public stopPolygonDrawing() {
    this.polygonManager.stopPolygonDrawing();
    this.drawingMode = false;
    this.historyManager.clear();
  }

  public zoomIn() {
    this.mapManager.zoomIn();
  }

  public zoomOut() {
    this.mapManager.zoomOut();
  }

  public centerMap() {
    this.mapManager.centerMap(this.center as [number, number], this.zoom);
  }



  public activateSplitMode() {
    this.drawingMode = false;
    this.splitManager.activateSplitMode(null, null);
  }

  public deactivateSplitMode() {
    this.splitManager.deactivateSplitMode();
  }

  /**
   * Swap old/new labels on the split polygons so the user can choose
   * which polygon is old (stays) and which is new.
   */
  public swapSplitIds() {
    this.splitManager.swapSplitIds();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.polygonManager?.stopPolygonDrawing();
    this.siblingPolygonManager?.destroy();
    if (this.ndiLayer) {
      this.mapManager.removeLayer(this.ndiLayer);
      this.ndiLayer = null;
    }
    this.mapManager?.destroy();
  }
}
